import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { 
    LayoutDashboard, 
    GraduationCap, 
    Users, 
    BookOpen, 
    Layers, 
    Trophy, 
    BarChart3, 
    Settings, 
    LogOut,
    Sun,
    Moon,
    UsersRound,
    ClipboardCheck,
    Target,
    Image,
    Sparkles,
    Presentation,
    FileText as FileTextIcon,
    BookMarked,
    UserCog,
    ExternalLink,
    Video,
    Briefcase,
    Mic,
    Crown,
    TrendingUp,
    Lock,
    Award,
    Medal,
    Ticket,
    Code,
    DollarSign,
    ClipboardList,
    ShieldCheck,
    Bell
} from 'lucide-react';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { cn } from '../../lib/utils';
import { getModuleIcon } from '../../lib/moduleIcons';

const BADGE_STYLES = {
    new: { pill: 'bg-red-600 text-white shadow-sm shadow-red-600/30', bell: 'origin-top animate-[wiggle_1s_ease-in-out_infinite]' },
    due: { pill: 'bg-amber-400 text-slate-950 shadow-sm shadow-amber-500/30', bell: 'animate-pulse' },
    open: { pill: 'bg-violet-600 text-white', bell: 'opacity-90' },
};

const plural = (n) => (n > 1 ? 's' : '');

const QualityTaskBadge = ({ count, newCount, dueSoon, active }) => {
    const state = newCount > 0 ? 'new' : dueSoon > 0 ? 'due' : 'open';
    const title = state === 'new' ? `${newCount} Quality Task${plural(newCount)} need${newCount > 1 ? '' : 's'} your attention`
        : state === 'due' ? `${dueSoon} Quality Task${plural(dueSoon)} due within 48 hours` : `${count} open Quality Task${plural(count)}`;
    return (
        <span
            className={cn(
                'relative ml-auto flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums transition-colors duration-300',
                state === 'open' && active ? 'bg-white/20 text-white' : BADGE_STYLES[state].pill
            )}
            title={title}
            aria-label={title}
            data-state={state}
            data-testid="quality-tasks-count-badge"
        >
            <Bell className={cn('h-3 w-3', BADGE_STYLES[state].bell)} data-testid="quality-tasks-bell-icon" />
            {count}
            {state === 'new' && (
                <span className="absolute -right-0.5 -top-0.5 flex h-2.5 w-2.5" data-testid="quality-tasks-new-dot">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-75" />
                    <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-amber-400 ring-2 ring-card" />
                </span>
            )}
        </span>
    );
};

export const Sidebar = ({ qualitySummary }) => {
    const { user, logout, isAdmin, isTrainer, api } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const location = useLocation();
    const [testLoading, setTestLoading] = useState(false);

    const isActive = (path) => location.pathname === path;
    const isActivePrefix = (prefix) => location.pathname.startsWith(prefix);

    const handleTestAsLearner = async () => {
        setTestLoading(true);
        try {
            const response = await api.post('/users/test-user/token');
            const { token } = response.data;
            const baseUrl = window.location.origin;
            const testUrl = `${baseUrl}/test-login?token=${token}`;
            window.open(testUrl, '_blank');
            toast.success('Opening learner view...', {
                description: 'A new tab will open with the Admin Trainee account'
            });
        } catch (error) {
            toast.error('Failed to start test session', {
                description: error.response?.data?.detail || 'Please try again'
            });
        } finally {
            setTestLoading(false);
        }
    };

    // State for dynamic course modules
    const [dynamicModules, setDynamicModules] = useState([]);

    // Fetch dynamic course modules
    const fetchDynamicModules = useCallback(async () => {
        if (!api) return;
        try {
            const response = await api.get('/course-modules');
            setDynamicModules(response.data.modules || []);
        } catch (error) {
            console.error('Failed to fetch dynamic modules:', error);
        }
    }, [api]);

    useEffect(() => {
        fetchDynamicModules();
    }, [fetchDynamicModules]);

    // Check if user has access to a dynamic module
    const hasDynamicModuleAccess = (moduleSlug) => {
        if (isAdmin) return true;
        const featureKey = moduleSlug.replace(/-/g, '_');
        return user?.feature_access?.[featureKey] !== false;
    };

    // Main navigation items for all users
    // Check feature access - admins always have access to everything
    const hasStaffingAccess = isAdmin || user?.feature_access?.staffing_training !== false;
    const hasCommunicationAccess = isAdmin || user?.feature_access?.communication_training !== false;
    const hasLeadershipAccess = isAdmin || user?.feature_access?.leadership_training !== false;
    const hasInsideSalesAccess = isAdmin || user?.feature_access?.inside_sales_training !== false;
    const hasTechLearningAccess = isAdmin || user?.feature_access?.tech_learning !== false;
    const hasFinanceTrainingAccess = isAdmin || user?.feature_access?.finance_training !== false;
    const showQualityTasks = hasStaffingAccess;
    const isQualityAdmin = isAdmin || user?.feature_access?.quality_admin === true;
    
    
    const mainNavItems = [
        { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    ];
    
    // Add training programs based on feature access
    if (hasStaffingAccess) {
        mainNavItems.push({ 
            path: '/staffing-training', 
            label: 'Staffing Training', 
            icon: Briefcase,
            highlight: true,
            description: 'All training modules'
        });
    }

    if (showQualityTasks) {
        mainNavItems.push({
            path: '/quality-tasks',
            label: 'Quality Tasks',
            icon: ClipboardList,
            badge: qualitySummary?.open || 0,
            badgeNew: qualitySummary?.new || 0,
            badgeDue: qualitySummary?.due_soon || 0
        });
    }
    
    if (hasCommunicationAccess) {
        mainNavItems.push({ 
            path: '/communication-training', 
            label: 'Communication Training', 
            icon: Mic,
            description: 'Presentation & voice training'
        });
    }
    
    if (hasLeadershipAccess) {
        mainNavItems.push({ 
            path: '/leadership-training', 
            label: 'Leadership Training', 
            icon: Crown,
            description: 'Leadership development'
        });
    }
    
    if (hasInsideSalesAccess) {
        mainNavItems.push({
            path: '/inside-sales',
            label: 'Inside Sales Training',
            icon: TrendingUp,
            highlight: true,
            description: 'B2B sales mastery program'
        });
    }
    
    if (hasTechLearningAccess) {
        mainNavItems.push({
            path: '/tech-learning',
            label: 'Tech Learning',
            icon: BookMarked,
            highlight: true,
            description: 'Full Stack, AI/ML, GenAI courses'
        });
    }
    
    if (hasFinanceTrainingAccess) {
        mainNavItems.push({
            path: '/finance-training',
            label: 'Finance Training',
            icon: DollarSign,
            highlight: true,
            description: 'Finance & Accounting courses'
        });
    }

    // Add dynamic course modules (learner view)
    dynamicModules.forEach(module => {
        if (module.is_active && hasDynamicModuleAccess(module.slug)) {
            mainNavItems.push({
                path: `/courses/${module.slug}`,
                label: module.name,
                icon: getModuleIcon(module.icon),
                highlight: true,
                description: module.description || `${module.name} courses`
            });
        }
    });

    // Additional learner items
    const learnerNavItems = [
        { path: '/assessments', label: 'Assessments', icon: ClipboardCheck },
        { path: '/badges', label: 'Badges', icon: Award, highlight: true },
        { path: '/leaderboard', label: 'Leaderboard', icon: Medal },
        { path: '/my-tickets', label: 'My Tickets', icon: Ticket },
    ];

    // User-specific items (not for admin)
    const userOnlyItems = [
        { path: '/training', label: 'My Training', icon: GraduationCap },
        { path: '/learning-profile', label: 'My Profile', icon: Target },
        { path: '/communication-profile', label: 'Communication Score', icon: Mic, highlight: true },
    ];

    // Admin/Trainer items
    const adminNavItems = [
        { path: '/topics', label: 'Topics', icon: BookOpen },
        { path: '/modules', label: 'Modules', icon: Layers },
        { path: '/visual-aids', label: 'Visual Aids', icon: Image },
        { path: '/module-generator', label: 'AI Generator', icon: Sparkles },
        { path: '/presentations', label: 'Presentations', icon: Presentation },
        { path: '/reference-docs', label: 'Reference Docs', icon: FileTextIcon },
        { path: '/curriculum-builder', label: 'Curriculum Builder', icon: BookMarked },
        { path: '/admin/course-builder', label: 'Tech Course Builder', icon: Code, adminOnly: true, highlight: true },
        { path: '/admin/finance-course-builder', label: 'Finance Course Builder', icon: DollarSign, adminOnly: true, highlight: true },
    ];

    // Add dynamic course module builders (admin only)
    dynamicModules.forEach(module => {
        if (module.is_active) {
            adminNavItems.push({
                path: `/admin/module-builder/${module.slug}`,
                label: `${module.name} Builder`,
                icon: getModuleIcon(module.icon),
                adminOnly: true,
                highlight: true
            });
        }
    });

    // Add remaining admin items
    const qualityAdminItem = { path: '/quality-admin', label: 'Quality Admin', icon: ShieldCheck };
    if (isAdmin) adminNavItems.push({ ...qualityAdminItem, adminOnly: true });

    adminNavItems.push(
        { path: '/users', label: 'Users', icon: Users, adminOnly: true },
        { path: '/batches', label: 'Batches', icon: UsersRound, adminOnly: true },
        { path: '/reports', label: 'Reports', icon: BarChart3 },
        { path: '/admin/tickets', label: 'Support Tickets', icon: Ticket, adminOnly: true },
        { path: '/settings', label: 'Settings', icon: Settings, adminOnly: true }
    );

    const renderNavLink = (item) => {
        const Icon = item.icon;
        const active = isActive(item.path) || (item.path !== '/dashboard' && isActivePrefix(item.path));
        
        return (
            <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.path.replace(/\//g, '-').slice(1)}`}
                className={cn(
                    'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all',
                    active 
                        ? 'bg-primary text-primary-foreground shadow-md' 
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                )}
            >
                <Icon className="h-5 w-5 shrink-0" />
                <span className="truncate">{item.label}</span>
                {item.badge > 0 && <QualityTaskBadge count={item.badge} newCount={item.badgeNew} dueSoon={item.badgeDue} active={active} />}
            </Link>
        );
    };

    return (
        <aside className="fixed left-0 top-0 z-40 h-screen w-64 border-r border-border bg-card/50 backdrop-blur-xl">
            <div className="flex h-full flex-col">
                {/* Logo */}
                <div className="flex items-center gap-3 px-4 py-4">
                    <img 
                        src="/tekwissen-logo.png" 
                        alt="TekWissen" 
                        className="h-10 w-auto object-contain"
                        style={{ maxWidth: '140px' }}
                    />
                </div>

                <Separator />

                {/* Navigation */}
                <nav className="flex-1 space-y-1 px-3 py-4 overflow-y-auto">
                    {/* Main Nav - All Users */}
                    {mainNavItems.map(renderNavLink)}
                    
                    {/* Learner Items */}
                    {learnerNavItems.map(renderNavLink)}
                    
                    {/* Quality Admin for non-admin users granted access */}
                    {isQualityAdmin && !isAdmin && (
                        <>
                            <Separator className="my-3" />
                            <p className="px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                                Quality
                            </p>
                            {renderNavLink(qualityAdminItem)}
                        </>
                    )}

                    {/* User-only items (not admin) */}
                    {!isAdmin && !isTrainer && userOnlyItems.map(renderNavLink)}

                    {/* Admin/Trainer Items */}
                    {(isAdmin || isTrainer) && (
                        <>
                            <Separator className="my-3" />
                            <p className="px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                                Administration
                            </p>
                            {adminNavItems
                                .filter(item => !item.adminOnly || isAdmin)
                                .map(renderNavLink)
                            }
                        </>
                    )}
                    
                    {/* Test as Learner Button - Only for Admin/Trainer */}
                    {(isAdmin || isTrainer) && (
                        <>
                            <Separator className="my-3" />
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleTestAsLearner}
                                disabled={testLoading}
                                className="w-full justify-start gap-3 border-dashed border-primary/50 hover:bg-primary/10 hover:border-primary text-primary"
                                data-testid="test-as-learner-btn"
                            >
                                <UserCog className="h-5 w-5" />
                                <span className="flex-1 text-left">Test as Learner</span>
                                <ExternalLink className="h-4 w-4 opacity-50" />
                            </Button>
                        </>
                    )}
                </nav>

                <Separator />

                {/* User Section */}
                <div className="p-4 space-y-3">
                    {/* Theme Toggle */}
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={toggleTheme}
                        className="w-full justify-start gap-3"
                        data-testid="theme-toggle"
                    >
                        {theme === 'dark' ? (
                            <>
                                <Sun className="h-5 w-5" />
                                <span>Light Mode</span>
                            </>
                        ) : (
                            <>
                                <Moon className="h-5 w-5" />
                                <span>Dark Mode</span>
                            </>
                        )}
                    </Button>

                    {/* User Info */}
                    <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-muted/50">
                        <div className="h-9 w-9 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white font-semibold text-sm">
                            {user?.name?.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{user?.name}</p>
                            <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
                        </div>
                    </div>

                    {/* Logout */}
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={logout}
                        className="w-full justify-start gap-3 text-destructive hover:text-destructive hover:bg-destructive/10"
                        data-testid="logout-btn"
                    >
                        <LogOut className="h-5 w-5" />
                        <span>Logout</span>
                    </Button>
                </div>
            </div>
        </aside>
    );
};
